import React, { useMemo, useState, useCallback } from "react";
import { styled, alpha } from "@mui/material/styles";
import ChatHeader from "./ChatHeader";
import MessageField from "./MessageField";
import useApp from "../../../store/contexts/AppContext";
import { CHAT_ROOMS } from "../../../config/index";

// Styled container for the active chat with proper flex layout
const ActiveChatContainer = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  background: theme.palette.mode === "dark"
    ? `linear-gradient(180deg, ${alpha("#0f1419", 1)} 0%, ${alpha("#0a0e13", 1)} 100%)`
    : `linear-gradient(180deg, ${alpha("#ffffff", 1)} 0%, ${alpha("#f5f7fa", 1)} 100%)`,
}));

export default function ActiveChat({ innerRoom }) {
  const {
    appState: { activeChat, userData },
  } = useApp();

  const [triggerSearch, setTriggerSearch] = useState(0);

  const handleSearchClick = useCallback(() => {
    setTriggerSearch(prev => prev + 1);
  }, []);

  const otherUserForDirectChat = useMemo(() => {
    if (!activeChat?.room_participants) {
      // Group chats can still show their name; single chats without participants aren't renderable
      return activeChat?.type === CHAT_ROOMS.TYPES.GROUP ? { username: activeChat?.name } : null;
    }

    return activeChat?.type === CHAT_ROOMS.TYPES.SINGLE
      ? activeChat.room_participants.find(
          (participant) =>
            participant.id?.toString() !== userData.id?.toString()
        )
      : { username: activeChat.name };
  }, [
    activeChat?.room_participants,
    activeChat?.type,
    activeChat?.name,
    userData.id,
  ]);

  // Get room moderators if it's a group chat
  const roomModerators = useMemo(() => {
    if (
      activeChat?.type === CHAT_ROOMS.TYPES.GROUP &&
      activeChat?.room_participants
    ) {
      return activeChat.room_participants.filter(
        (participant) =>
          participant.is_moderator === 1 || participant.is_admin === 1
      );
    }
    return [];
  }, [activeChat?.type, activeChat?.room_participants]);

  if (!activeChat || Object.keys(activeChat).length === 0) return null;
  // Don't render with a generic "User" header — wait until participant data is available
  if (activeChat.type === CHAT_ROOMS.TYPES.SINGLE && !otherUserForDirectChat?.username) return null;

  return (
    <ActiveChatContainer>
      <ChatHeader
        otherUser={otherUserForDirectChat}
        innerRoom={innerRoom}
        onSearchClick={handleSearchClick}
        hasConnectedBefore={activeChat?.has_connected_before}
        messageCount={activeChat?.messages?.length || 0}
      />
      <MessageField
        otherUser={otherUserForDirectChat}
        otherUserOnline={otherUserForDirectChat?.is_online === 1}
        innerRoom={innerRoom}
        roomModerators={roomModerators}
        triggerSearch={triggerSearch}
      />
    </ActiveChatContainer>
  );
}
